import type { WorkspaceItem } from '@/hooks/useWorkspace';
import type { TreeItem } from '@/lib/tree/types';

export const listToTree = (input: WorkspaceItem[]): TreeItem[] => {
  // Sort input based on displayOrder
  const sortedInput = sortByDisplayOrder(input);

  const map = new Map<string, TreeItem>();

  // Create all items first
  // biome-ignore lint/complexity/noForEach: <explanation>
  sortedInput.forEach((item) => {
    const treeItem = makeTreeItem(item);
    map.set(item.id, treeItem);
  });

  // Build relationships
  // biome-ignore lint/complexity/noForEach: <explanation>
  sortedInput.forEach((item) => {
    if (item.groupId && map.has(item.groupId)) {
      const parent = map.get(item.groupId) as TreeItem;
      const child = map.get(item.id) as TreeItem;
      parent.children.push(child);
    }
  });

  // Return only root items (those without parents or with non-existent parents)
  const rootItems = sortedInput
    .filter((item) => !item.groupId || !map.has(item.groupId))
    .map((item) => map.get(item.id) as TreeItem);

  return rootItems;
};

export const makeTreeItem = (item: WorkspaceItem): TreeItem => {
  return {
    id: item.id,
    name: item.name ?? '',
    type: item.type,
    children: [],
    teamId: item.teamId,
    collapsed: item.groupCollapsed ?? false,
    chatbot: item,
  };
};

/**
 * Sorts an array of items by their displayOrder property
 * @param items Array of items with displayOrder properties
 * @returns A new array with items ordered by displayOrder
 */
export const sortByDisplayOrder = (items: WorkspaceItem[]) => {
  if (items.length === 0) return [];

  // First, separate items with displayOrder = -1 (priority items)
  const priorityItems = items.filter((item) => item.displayOrder === -1);

  // Filter out the priority items from the main list
  const normalItems = items.filter((item) => item.displayOrder !== -1);

  // Sort normal items by displayOrder
  const sortedNormalItems = [...normalItems].sort((a, b) => {
    // If both items have display order, sort by it
    if (a.displayOrder !== null && b.displayOrder !== null) {
      return a.displayOrder - b.displayOrder;
    }
    // If only one has display order, prioritize the one with display order
    if (a.displayOrder !== null) return -1;
    if (b.displayOrder !== null) return 1;
    // If neither has display order, maintain original order (stable sort)
    return 0;
  });

  // Combine priority items first, then sorted normal items
  return [...priorityItems, ...sortedNormalItems];
};
